import { addDays } from 'date-fns';
import {
    AlertTriangle, FolderOpen, Search, ClipboardCheck,
    FileCheck, FolderCheck, FolderClosed, Check,
    Hourglass, ChevronLeft, ChevronRight
} from 'lucide-react';
import { useState, useEffect, type ComponentType, type SVGProps } from 'react';

import { PictoEconomie } from '@/assets/icons-april-fr/pictos';
import { Card, CardContent } from '@/components/ui/card';
import { toDate } from '@/lib/dates';
import type { Accident } from '@/types/api/accident';

const defineStepAccident = (accident: Accident) => {
    let step = 0;
    const documents = accident.documents ?? [];
    const dateRapportExpert = accident.dateRapportExpert as string | null | undefined;

    if (accident.dateOuverture) {
        step = 1;
    } else {
        return step;
    }

    if (accident.datePassageExpert) {
        step = 2;
    } else {
        return step;
    }

    if (toDate(accident.datePassageExpert).getTime() <= addDays(new Date(), 15).getTime()) {
        step = 3;
    } else {
        return step;
    }

    if (dateRapportExpert) {
        step = 4;
    } else {
        return step;
    }

    if (toDate(dateRapportExpert).getTime() <= addDays(new Date(), 10).getTime()) {
        step = 5;
    } else {
        return step;
    }

    if (documents.length > 0 && documents.every((d) => d.recu === true) && documents.every((d) => d.traite === true)) {
        step = 6;
    } else {
        return step;
    }

    if (accident.reglements.length > 0 && accident.reglements[accident.reglements.length - 1].reference === "VALIDE") {
        step = 7;
    } else {
        return step;
    }

    if (accident.dateFermeture) {
        step = 8;
    } else {
        return step;
    }

    return step;
};

const events: { description: string; icon: ComponentType<SVGProps<SVGSVGElement>> }[] = [
    { description: 'Déclaration', icon: AlertTriangle },
    { description: 'Dossier ouvert', icon: FolderOpen },
    { description: "Passage de l'expert", icon: Search },
    { description: "Estimation de l'expert", icon: ClipboardCheck },
    { description: "Rapport de l'expert reçu", icon: FileCheck },
    { description: 'Analyse du dossier', icon: FolderOpen },
    { description: 'Dossier complet', icon: FolderCheck },
    { description: 'Indemnisation', icon: PictoEconomie },
    { description: 'Clôture du dossier', icon: FolderClosed },
];

export interface TimelineProps {
    accident: Accident;
}

const Timeline = ({ accident }: TimelineProps) => {
    const currentStep = defineStepAccident(accident);

    const getVisibleCount = () => (window.innerWidth < 640 ? 3 : 5);
    const [visibleCount, setVisibleCount] = useState(getVisibleCount());

    useEffect(() => {
        const handleResize = () => setVisibleCount(getVisibleCount());
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const getInitialIndex = () => {
        if (visibleCount === 3) return Math.max(0, Math.min(currentStep, events.length - visibleCount));
        if (currentStep > 5) return Math.max(0, events.length - visibleCount);
        return Math.max(0, currentStep - 1);
    };

    const [startIndex, setStartIndex] = useState(getInitialIndex());

    const handlePrev = () => {
        if (startIndex > 0) setStartIndex(startIndex - 1);
    };

    const handleNext = () => {
        if (startIndex < events.length - visibleCount) setStartIndex(startIndex + 1);
    };

    const visibleEvents = events.slice(startIndex, startIndex + visibleCount);

    return (
        <Card>
            <CardContent className="py-6">
                <div className="flex items-center gap-2">
                    {/* Prev */}
                    <button
                        onClick={handlePrev}
                        disabled={startIndex === 0}
                        className="p-2 rounded-full hover:bg-muted transition-colors disabled:opacity-30 cursor-pointer shrink-0"
                        aria-label="Étape précédente"
                    >
                        <ChevronLeft className="w-5 h-5 text-primary" aria-hidden="true" />
                    </button>

                    {/* Steps */}
                    <div className="flex-1 flex items-start">
                        {visibleEvents.map((event, index) => {
                            const globalIndex = startIndex + index;
                            const isCompleted = currentStep > globalIndex;
                            const isActive = currentStep === globalIndex;
                            const isFirst = index === 0;
                            const isLast = index === visibleEvents.length - 1;
                            const Icon = event.icon;

                            return (
                                <div key={globalIndex} className="flex-1 flex flex-col items-center relative">
                                    {/* Connector line */}
                                    {!isFirst && (
                                        <div className="absolute top-5 right-1/2 w-full h-0.5 -z-0">
                                            <div className={`h-full ${currentStep > globalIndex ? 'bg-primary' : 'bg-border'}`} />
                                        </div>
                                    )}
                                    {!isLast && (
                                        <div className="absolute top-5 left-1/2 w-full h-0.5 -z-0">
                                            <div className={`h-full ${currentStep > globalIndex + 1 ? 'bg-primary' : currentStep === globalIndex + 1 ? 'bg-primary/30' : 'bg-border'}`} />
                                        </div>
                                    )}

                                    {/* Bubble */}
                                    <div className="relative z-10 w-10 h-10 rounded-full bg-card shrink-0">
                                        <div className={`absolute inset-0 flex items-center justify-center rounded-full border-2 transition-all duration-300 ${
                                            isCompleted
                                                ? 'bg-primary border-primary text-white'
                                                : isActive
                                                    ? 'bg-destructive border-destructive text-white animate-pulse'
                                                    : 'bg-muted border-border text-muted-foreground'
                                        }`}>
                                            {isCompleted ? (
                                                <Check className="w-5 h-5" />
                                            ) : isActive ? (
                                                <Hourglass className="w-4 h-4" />
                                            ) : (
                                                <Icon className="w-4 h-4" />
                                            )}
                                        </div>
                                    </div>

                                    {/* Label */}
                                    <p className={`mt-2 text-xs text-center leading-tight max-w-[100px] ${
                                        isCompleted || isActive ? 'text-foreground font-medium' : 'text-muted-foreground'
                                    }`}>
                                        {event.description}
                                    </p>
                                </div>
                            );
                        })}
                    </div>

                    {/* Next */}
                    <button
                        onClick={handleNext}
                        disabled={startIndex >= events.length - visibleCount}
                        className="p-2 rounded-full hover:bg-muted transition-colors disabled:opacity-30 cursor-pointer shrink-0"
                        aria-label="Étape suivante"
                    >
                        <ChevronRight className="w-5 h-5 text-primary" aria-hidden="true" />
                    </button>
                </div>
            </CardContent>
        </Card>
    );
};

export default Timeline;
